import {
  type PackageItinerarySummary,
  ReservationEnum,
  ReservationSummaryEnum,
} from "@b2bportal/rbc-bundled-packages-api";

type GroundWrapper = { reservationId: string };

type PackageWrapper = { reservation: PackageItinerarySummary };

/**
 * Collects the ground reservation ids belonging to packages.
 *
 * Current itinerary summaries expose the car id directly. Older payloads carry
 * an opaque ground reservation, so keep that fallback for compatibility.
 */
export const collectPackageGroundIds = (
  ...wrapperLists: Array<PackageWrapper[] | undefined>
): Set<string> => {
  const ids = new Set<string>();
  for (const wrappers of wrapperLists) {
    for (const { reservation } of wrappers ?? []) {
      for (const member of reservation?.reservations ?? []) {
        if (member.ReservationSummary === ReservationSummaryEnum.Car) {
          if (typeof member.id === "string") ids.add(member.id);
          continue;
        }
        if (
          !("Reservation" in member) ||
          member.Reservation !== ReservationEnum.Ground
        )
          continue;
        const ground = (member as { ground?: Record<string, unknown> }).ground;
        const id =
          ground?.["internalGroundBookingId"] ??
          (ground?.["reservation"] as Record<string, unknown> | undefined)?.[
            "internalGroundBookingId"
          ];
        if (typeof id === "string") ids.add(id);
      }
    }
  }
  return ids;
};

/**
 * Drops cars that a package already accounts for.
 *
 * The flight and lodging itinerary endpoints exclude package members before the
 * portal sees them; `/api/v1/ground/itineraries` does not, and the trips list
 * renders one row per reservation with no dedupe, so the car would appear both
 * inside its package and on its own.
 */
export const withoutPackageCars = <R extends { data?: GroundWrapper[] }>(
  response: R,
  packageGroundIds: Set<string>
): R =>
  response.data
    ? {
        ...response,
        data: response.data.filter(
          (wrapper) => !packageGroundIds.has(wrapper.reservationId)
        ),
      }
    : response;
